import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMe, useUpdateMeCache } from '../../hooks/useMe'
import { UserAPI } from '../../api/api-switcher'

type UseBirthDateInputOptions = {
  onSuccess?: () => void
  onError?: (error: string) => void
}

export const useBirthDateInput = ({ onSuccess, onError }: UseBirthDateInputOptions = {}) => {
  const { t } = useTranslation()
  const me = useMe()
  const updateCache = useUpdateMeCache()
  const [year, setYear] = useState('')
  const [month, setMonth] = useState('')
  const [day, setDay] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [is_submitting, setIsSubmitting] = useState(false)

  // 初期値を設定
  useEffect(() => {
    if (me?.birth_date) {
      const birth_date = new Date(me.birth_date)
      setYear(birth_date.getFullYear().toString())
      setMonth((birth_date.getMonth() + 1).toString())
      setDay(birth_date.getDate().toString())
    }
  }, [me?.birth_date])

  const currentYear = new Date().getFullYear()

  // リアルタイムで日付の妥当性をチェック
  useEffect(() => {
    if (!year || !month || !day) {
      setError(null)
      return
    }

    const yearNum = parseInt(year)
    const monthNum = parseInt(month)
    const dayNum = parseInt(day)

    // 数値の範囲チェック
    if (yearNum < 1900 || yearNum > currentYear) {
      setError(t('sensitive.birth_date_invalid'))
      return
    }

    if (monthNum < 1 || monthNum > 12) {
      setError(t('sensitive.birth_date_invalid'))
      return
    }

    if (dayNum < 1 || dayNum > 31) {
      setError(t('sensitive.birth_date_invalid'))
      return
    }

    const birth_date = new Date(yearNum, monthNum - 1, dayNum)

    // 日付の妥当性チェック（例：2月31日などの無効な日付）
    if (birth_date.getFullYear() !== yearNum ||
        birth_date.getMonth() !== monthNum - 1 ||
        birth_date.getDate() !== dayNum) {
      setError(t('sensitive.birth_date_invalid'))
      return
    }

    // 未来の日付チェック
    if (birth_date > new Date()) {
      setError(t('sensitive.birth_date_invalid'))
      return
    }

    setError(null)
  }, [year, month, day, currentYear, t])

  const handleSubmit = useCallback(async () => {
    if (!year || !month || !day) {
      setError(t('sensitive.birth_date_required'))
      return
    }

    // エラーがある場合は保存しない
    if (error) {
      return
    }

    setIsSubmitting(true)

    const yearNum = parseInt(year)
    const monthNum = parseInt(month)
    const dayNum = parseInt(day)
    const birth_date = new Date(yearNum, monthNum - 1, dayNum)

    const { error: api_error, response } = await UserAPI.updateBirthDate(birth_date)

    setIsSubmitting(false)

    if (api_error || !response.ok) {
      console.error('Birth date update failed:', api_error)
      const error_msg = t('general.error_occurred')
      setError(error_msg)
      onError?.(error_msg)
    } else {
      updateCache({birth_date})
      onSuccess?.()
    }
  }, [year, month, day, error, t, onError, onSuccess, updateCache])

  const can_submit = !!year && !!month && !!day && !error && !is_submitting

  const submit_state: 'can' | 'disabled' | 'submitting' =
    is_submitting ? 'submitting' :
    can_submit ? 'can' :
    'disabled'

  return {
    year,
    month,
    day,
    error,
    is_submitting,
    currentYear,
    can_submit,
    submit_state,
    setYear,
    setMonth,
    setDay,
    handleSubmit
  }
}
